import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TMaterialsFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import RHFUploadDynamic from 'src/components/hook-form/RHFUploadDynamic';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { IMaterials, useStores } from 'src/models';
import { materialsApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getMaterialsFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import removeNullUndefined from 'src/utils/removeNullUndefined';
import { separateFilesAndPaths } from 'src/utils/separateFilesAndPaths';

interface IMaterialFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IMaterials;
}

export const MaterialsFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IMaterialFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const { getLang } = useGetSelectOption();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;

    const [files, setFiles] = useState<Array<File | string>>([]);

    useEffect(() => {
      setFiles(selectedData?.filePath || []);
    }, []);

    const methods = useForm<TMaterialsFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: getMaterialsFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: TMaterialsFormData) => {
      // files의 upload파일과 기존이미지path 추출
      const { files: uploadFiles, paths: existingPaths } = separateFilesAndPaths(files || []);

      // 삭제할이미지path 추출
      const originalPaths = selectedData?.filePath || [];
      const deleteFilesPath = originalPaths.filter((path) => !existingPaths.includes(path));

      const formData = {
        // 공통
        ...(uploadFiles.length > 0 && { files: uploadFiles }),
        ...removeNullUndefined<TMaterialsFormData>({
          ...data,
          files: uploadFiles.length > 0 ? uploadFiles : undefined,
        }),
        ...(isCreate
          ? {}
          : {
              filePath: existingPaths.length > 0 ? existingPaths : undefined,
              ...(deleteFilesPath.length > 0 && { deleteFilesPath }),
            }),
      };

      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate
        ? materialsApi.post(formData)
        : materialsApi.put(selectedData?.materialSid ?? 0, formData);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (!selectedData?.materialSid) return;
        materialsApi
          .remove(selectedData?.materialSid)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '자료 삭제',
        content: '해당 자료를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('materials.form.langCd')}
                  name="langCd"
                  options={getLang()}
                  useAll={false}
                />
              </FormItem>
              <FormItem>
                <RHFSwitch label={t('materials.form.useYn')} name="useYn" />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField label={t('materials.form.title')} name="title" disabled={readonly} />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.content')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFUploadDynamic
                  name="filePath"
                  files={files}
                  onChange={(newFile) => setFiles(newFile)}
                  accept={{
                    'image/jpeg': [],
                    'image/jpg': [],
                    'image/png': [],
                    'application/pdf': [], // PDF 파일
                    'application/vnd.ms-excel': [], // Excel 파일 (.xls)
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // Excel 파일 (.xlsx)
                    'application/xlsx': [], // Excel 파일 (.xlsx)
                    'application/msword': [], // Word 파일 (.doc)
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // Word 파일 (.docx)
                    'application/zip': [], // ZIP 파일
                    'application/x-zip-compressed': [], // ZIP 파일 (다른 MIME 타입)
                  }}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
        </DialogContent>
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
